/**
 * @file: ContactCardRenderer.tsx
 *
 * A shared contact (`contact_card` payload). Full form is an avatar + name +
 * title card; `quoted` is a compact "person icon + name" chip.
 */

import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayloadContactCard } from '@euler/model/chat';
import { User } from 'lucide-react-native';
import { FC, memo, useCallback } from 'react';
import { View } from 'react-native';
import { CardFooter, CardQuoteChip, CardSurface } from './cardPrimitives';
import { PayloadRendererProps } from './types';

interface Props extends PayloadRendererProps<ChatMessagePayloadContactCard> {
  onOpenCard?: (payload: ChatMessagePayloadContactCard) => void;
}

export const ContactCardRenderer: FC<Props> = memo(
  ({ payload, quoted, onOpenCard }) => {
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(
      () => onOpenCard?.(payload),
      [onOpenCard, payload],
    );

    if (quoted) {
      return (
        <CardQuoteChip
          icon={<User color="#94A3B8" size={rpx(15)} />}
          label={payload.name ?? '联系人'}
        />
      );
    }

    return (
      <CardSurface onPress={handlePress}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Avatar
            uri={payload.avatar ?? undefined}
            name={payload.name ?? '?'}
            style={{
              width: rpx(40),
              height: rpx(40),
              borderRadius: rpx(20),
              marginRight: rpx(10),
            }}
          />
          <View style={{ flex: 1 }}>
            <Typography.Text
              size={rpx(15)}
              color="#1E293B"
              numberOfLines={1}
              style={{ fontFamily: FontFamily.NotoSans.Medium }}
            >
              {payload.name ?? '联系人'}
            </Typography.Text>
            {payload.title ? (
              <Typography.Text
                size={rpx(12)}
                color="#94A3B8"
                numberOfLines={1}
                style={{ marginTop: rpx(2) }}
              >
                {payload.title}
              </Typography.Text>
            ) : null}
          </View>
          <User color="#94A3B8" size={rpx(20)} />
        </View>
        <CardFooter label="个人名片" />
      </CardSurface>
    );
  },
);
